import { Navigate, useNavigate } from 'react-router-dom'

import { loginPin } from '@/features/auth/api/pinLogin'
import { formatPinLoginError, isDeviceRevokedError, PIN_LOGIN_COPY } from '@/features/auth/pinLoginErrors'
import { SwitchDeviceFooter } from '@/features/auth/SwitchDeviceFooter'
import { warehouseTerms } from '@/features/warehouse/warehouseTerms'
import { PinEntryCard } from '@/shared/components/PinEntryCard'
import { clearDeviceUid } from '@/shared/auth/deviceUid'
import { clearWarehouseDevice, getWarehouseDevice, hasWarehouseDevice } from '@/shared/auth/warehouseDeviceStorage'
import { clearWarehouseSession, hasWarehouseSession, setWarehouseSession } from '@/shared/auth/warehouseSessionStorage'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

export function WarehouseLoginPage() {
  const navigate = useNavigate()
  const hydrated = useClientHydrated()

  if (hydrated && !hasWarehouseDevice()) {
    return <Navigate to="/warehouse/activate" replace />
  }
  if (hydrated && hasWarehouseSession()) {
    return <Navigate to="/warehouse/home" replace />
  }

  const device = hydrated ? getWarehouseDevice() : null

  function resetLocalDevice() {
    clearWarehouseSession()
    clearWarehouseDevice()
    clearDeviceUid()
  }

  async function handleSubmit(pin: string) {
    if (!device) throw new Error(PIN_LOGIN_COPY.loginFailed)

    try {
      const res = await loginPin({ device_id: device.deviceId, pin })
      if (
        res.user.role_code !== 'HEAD_WAREHOUSE' &&
        res.user.role_code !== 'ASSISTANT_HEAD_WAREHOUSE' &&
        res.user.role_code !== 'STAFF_WAREHOUSE'
      ) {
        throw new Error(warehouseTerms.login.roleNotAllowed)
      }
      setWarehouseSession({
        accessToken: res.access_token,
        userId: res.user.id,
        userName: res.user.name,
        roleCode: res.user.role_code as 'HEAD_WAREHOUSE' | 'ASSISTANT_HEAD_WAREHOUSE' | 'STAFF_WAREHOUSE',
        authority: res.authority,
        headPresent: res.head_present,
        brandId: res.brand_id,
        brandCode: res.brand_code,
        warehouseId: res.warehouse_id ?? device.warehouseId,
        loggedInAt: new Date().toISOString(),
      })
      navigate('/warehouse/home', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : PIN_LOGIN_COPY.loginFailed
      if (isDeviceRevokedError(message)) {
        resetLocalDevice()
        navigate('/warehouse/activate', { replace: true })
        return
      }
      throw new Error(formatPinLoginError(message))
    }
  }

  return (
    <PinEntryCard
      icon="warehouse"
      deviceLabel={device ? `${device.warehouseName} · ${device.deviceName}` : undefined}
      title={warehouseTerms.login.title}
      subtitle={warehouseTerms.login.subtitle}
      helper={warehouseTerms.login.helper}
      submitLabel={warehouseTerms.login.submit}
      submittingLabel={warehouseTerms.login.submitting}
      onSubmit={handleSubmit}
      footer={<SwitchDeviceFooter activateHref="/warehouse/activate" onReset={resetLocalDevice} />}
    />
  )
}
